import React, {useState, useCallback} from 'react';
import moment from 'moment';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import ky from 'ky';
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import BackspaceIcon from '@mui/icons-material/Backspace';

import TelemedicineTableCharges from './table';

import clients from '../../../data/clients';

import dateISOTimezone from '../../../helpers/dateISOTimezone';

interface FormattedClient {
    clientName: string,
    projectId: string,
    projectURL: string,
}

interface SmsResponseApi {
    count: number,
    rows?: Array<Record<string, unknown>>
}

interface ChimeReportResponseApi {
  count: number
  averageDurationInSeconds: number,
  statuses: {
    finished: number,
  };
}

interface IntegrationAPICallsResponseApi {
  count: number,
  totalDurationInMs: number,
  totalContentLengthInBytes: number,
}

interface RecordingSizeResponseApi {
  contentSizeInGigabytes: number,
}

interface TotalDoctorResponseApi {
  totalHealthProfessionals: number,
  totalActiveHealthProfessionals: number
}

interface IntegrationApiResponse {
  billing?: {
    totalPrice?: number,
    estimate?: number,
  },
}

const formattedClients = Object.keys(clients)
    .map((clientKey: string) => {
      const clientName = clients[clientKey].functionsConfig[1]
          .replace('smtp.clientname=', '')
          .replace('"', '')
          .replace('"', '');

      const projectId = clients[clientKey].firebaseConfig.projectId;
      const projectURL = `https://${projectId}.firebaseapp.com`;

      return {
        clientName,
        projectId,
        projectURL,
      } as FormattedClient;
    });

const TelemedicineCharges = () => {
  const [
    startDate,
    setStartDate,
  ] = useState<moment.Moment | null>(moment().startOf('month'));

  const [
    endDate,
    setEndDate,
  ] = useState<moment.Moment | null>(moment().endOf('month'));

  const [loading, setLoading] = useState(false);
  const [clientUsages, setClientUsages] = useState<TelemedicineCharges.RowData[]>([]);

  const fetchChargesByProjectId = useCallback(async (
      startDate,
      endDate,
  ) => {
    setLoading(true);

    const getClientUsage = async (
        client: FormattedClient,
    ):Promise<TelemedicineCharges.RowData> => {
      const {
        projectURL,
        projectId,
        clientName,
      } = client;

      let totalSms = 0;
      let totalFinishedAnamnesis = 0;
      let totalStartedAnamnesis = 0;
      // let totalDurationInMinutes = 0;
      let recordingSizeInGb = 0;
      let totalRecordingSizeInGb = 0;
      let totalHealthProfessionals = 0;
      let totalActiveHealthProfessionals = 0;
      let averageAnamnesisDurationInMinutes = 0;
      let totalIntegrationAPICalls = 0;
      let totalIntegrationAPICallsDurationInMs = 0;
      let totalIntegrationAPICallsContentLengthInBytes = 0;
      let billingEstimate = 0;
      let billingUsagePercent = 0;

      await Promise.all([
        ky.get(`${projectURL}/api/sms`, {
          headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
          searchParams: {
            startDate: dateISOTimezone(startDate.toDate()) + '-03:00',
            endDate: dateISOTimezone(endDate.toDate()) + '-03:00',
          },
          timeout: 300000,
        })
            .then(async (res) => {
              const body = await res.json() as SmsResponseApi;

              return body;
            })
            .then((body) => {
              totalSms = body?.count && projectId !== 'telemed-unimedvtrp' ? body?.count : 0;
            })
            .catch((e) => {
              console.error(e);
            }),
        ky.get(
            `${
              process.env.REACT_APP_TELEMED_ELASTIC_URL
            }/anamnesis/reports/chime`, {
              headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
              searchParams: {
                startDate: dateISOTimezone(startDate.toDate()) + '-03:00',
                endDate: dateISOTimezone(endDate.toDate()) + '-03:00',
                projectId,
              },
              timeout: 300000,
            })
            .then(async (res) => {
              const body = await res.json() as ChimeReportResponseApi;

              return body;
            })
            .then((body) => {
              totalStartedAnamnesis = body?.count ?? 0;
              totalFinishedAnamnesis = body?.statuses?.finished ?? 0;
              // totalDurationInMinutes = (body?.totalDurationInSeconds ?? 0) / 60;
              averageAnamnesisDurationInMinutes = (
                body?.averageDurationInSeconds ?? 0
              ) / 60;
            })
            .catch((e) => {
              console.error(e);
            }),
        ky.get(
            `${
              process.env.REACT_APP_TELEMED_ELASTIC_URL
            }/integrationAPICalls/getStatistics`, {
              headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
              searchParams: {
                startDate: dateISOTimezone(startDate.toDate()) + '-03:00',
                endDate: dateISOTimezone(endDate.toDate()) + '-03:00',
                projectId,
              },
              timeout: 300000,
            })
            .then(async (res) => {
              const body = await res.json() as IntegrationAPICallsResponseApi;

              return body;
            })
            .then((body) => {
              totalIntegrationAPICalls = body?.count ?? 0;
              totalIntegrationAPICallsDurationInMs = body?.totalDurationInMs ?? 0;
              totalIntegrationAPICallsContentLengthInBytes = body?.totalContentLengthInBytes ?? 0;
            })
            .catch((e) => {
              console.error(e);
            }),
        ky.get(`${projectURL}/api/anamnesis/recordings/size`, {
          headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
          searchParams: {
            startDate: dateISOTimezone(startDate.toDate()) + '-03:00',
            endDate: dateISOTimezone(endDate.toDate()) + '-03:00',
          },
          timeout: 300000,
        })
            .then(async (res) => {
              const body = await res.json() as RecordingSizeResponseApi;

              return body;
            })
            .then((body) => {
              recordingSizeInGb = body?.contentSizeInGigabytes ?? 0;
            })
            .catch((e) => {
              console.error(e);
            }),
        ky.get(
            `${projectURL}/api/anamnesis/recordings/size`, {
              headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
              searchParams: {
                startDate:
                    dateISOTimezone(
                        moment('2021-12-01')
                            .toDate(),
                    ) + '-03:00',
                endDate: dateISOTimezone(new Date()) + '-03:00',
              },
              timeout: 300000,
            })
            .then(async (res) => {
              const body = await res.json() as RecordingSizeResponseApi;

              return body;
            })
            .then((body:RecordingSizeResponseApi) => (
              totalRecordingSizeInGb = body?.contentSizeInGigabytes ?? 0
            ))
            .catch((e) => {
              console.error(e);
            }),
        ky.get(
            `${
              process.env.REACT_APP_TELEMED_ELASTIC_URL
            }/reports/getStatistics`, {
              headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
              searchParams: {
                projectId,
              },
              timeout: 300000,
            })
            .then(async (res) => {
              const body = await res.json() as TotalDoctorResponseApi;

              return body;
            })
            .then((body:TotalDoctorResponseApi) => {
              totalHealthProfessionals = body?.totalHealthProfessionals;
              totalActiveHealthProfessionals = body?.totalActiveHealthProfessionals;
            })
            .catch((e) => {
              console.error(e);

              return 0;
            }),
        ky.get(`${projectURL}/api/integration-api`, {
          headers: {rootkey: process.env.REACT_APP_TELEMED_API_ROOT_KEY},
          timeout: 300000,
        })
            .then(async (res) => (await res.json()) as IntegrationApiResponse[])
            .then((integrations) => {
              billingEstimate = integrations.reduce(
                  (sum, {billing}) => sum + (billing?.estimate ?? 0), 0,
              );
              billingUsagePercent = Math.min(100, integrations.reduce((sum, {billing}) => {
                const estimate = billing?.estimate ?? 0;
                const totalPrice = billing?.totalPrice ?? 0;

                return sum + (estimate > 0 ? Math.round((totalPrice / estimate) * 100) : 0);
              }, 0));
            })
            .catch((e) => {
              console.error(e);
            }),
      ]);


      return {
        clientName,
        projectId,
        totalSms,
        totalFinishedAnamnesis,
        totalStartedAnamnesis,
        totalHealthProfessionals,
        totalActiveHealthProfessionals,
        // totalDurationInMinutes: Math.ceil(
        //     parseFloat(totalDurationInMinutes
        //         .toFixed(2)),
        // ),
        recordingSizeInGb: parseFloat(recordingSizeInGb
            .toFixed(2)),
        totalRecordingSizeInGb: parseFloat(totalRecordingSizeInGb
            .toFixed(2)),
        averageAnamnesisDurationInMinutes: Math.ceil(
            parseFloat(averageAnamnesisDurationInMinutes.toFixed(2)),
        ),
        totalIntegrationAPICalls,
        totalIntegrationAPICallsDurationInMs,
        totalIntegrationAPICallsContentLengthInBytes,
        rowsLength: formattedClients.length,
        billingEstimate: parseFloat(billingEstimate.toFixed(2)),
        billingUsagePercent,
      };
    };

    const clientUsagesPromises = [];

    for (const client of formattedClients) {
      clientUsagesPromises
          .push(
              getClientUsage(client),
          );
    }

    await Promise
        .all(clientUsagesPromises)
        .then((clientUsages) => setClientUsages(clientUsages));
    setLoading(false);
  }, []);

  const handleSearch = async () => {
    if (
      startDate &&
      endDate
    ) {
      await fetchChargesByProjectId(
          startDate,
          endDate,
      );
    }
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setClientUsages([]);
    setLoading(false);
  };

  return (
    <Grid
      container
      spacing={6}
      style={{
        padding: '1em 2em 2em 2em',
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h4">
        Cobrança Telemedicina
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Data inicial"
            format="DD/MM/YYYY"
            value={startDate}
            onChange={(newValue: moment.Moment | null) => {
              if (newValue) {
                newValue.set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                });
              }

              setStartDate(newValue);
            }}
            inputRef={(params: any) => <TextField {...params} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Data final"
            format="DD/MM/YYYY"
            value={endDate}
            onChange={(newValue: moment.Moment | null) => {
              if (newValue) {
                newValue.set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999,
                });
              }

              setEndDate(newValue);
            }}
            inputRef={(params: any) => <TextField {...params} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <IconButton
          onClick={() => handleSearch()}
          disabled={!startDate && !endDate}
          color='primary'
        >
          <SearchIcon/> Buscar
        </IconButton>
      </Grid>
      <Grid item xs={3}>
        <IconButton
          onClick={() => handleClearFilters()}
          color='primary'
        >
          <BackspaceIcon/> Limpar
        </IconButton>
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          paddingBottom: '1em',
        }}
      >
        {
          loading &&
          <Grid
            container
            spacing={4}
            style={{
              justifyContent: 'center',
              alignContent: 'center',
              textAlign: 'center',
            }}
          >
            <Grid
              item
              xs={12}
            >
              <CircularProgress
                style={{
                  top: '50%',
                  right: '50%',
                  zIndex: 11,
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              Colhendo dados de todos os clientes...
            </Grid>
          </Grid>
        }
        {
          !loading &&
          clientUsages?.length > 0 &&
          <TelemedicineTableCharges
            rows={clientUsages}
          />
        }
      </Grid>
    </Grid>

  );
};

export default TelemedicineCharges;
